import { useAuthStore } from './auth';
import { SUPABASE_URL, SUPABASE_KEY } from './supabaseConfig';

// Post-RLS-hardening (migrations 0176/0177): all tables are authenticated-only.
// Every request carries the logged-in user's JWT (set at login into useAuthStore,
// persisted in localStorage). apikey stays the anon key (required by PostgREST);
// Authorization is the user's access_token so RLS runs as that user/tenant.
// Falls back to the anon key when logged out (returns empty under RLS — expected).
function authToken(): string {
  return useAuthStore.getState().accessToken || SUPABASE_KEY;
}

function authHeaders(extra: Record<string, string> = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: `Bearer ${authToken()}`,
    Accept: 'application/json',
    ...extra,
  };
}

// --- Token auto-refresh -----------------------------------------------------
// Supabase access tokens expire (~1h). Under locked RLS an expired token makes
// every call return 401/empty, which mid-demo looks like the data vanished.
// We transparently exchange the stored refresh_token for a fresh access_token
// and retry the request once. A single in-flight refresh is shared so a burst
// of parallel 401s triggers only one token exchange.
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  const store = useAuthStore.getState();
  const refresh_token = store.refreshToken;
  const user = store.user;
  if (!refresh_token || !user) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { apikey: SUPABASE_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.access_token) return null;
      store.setSession({
        user,
        accessToken: data.access_token,
        refreshToken: data.refresh_token ?? refresh_token,
        tenantSlug: store.tenantSlug ?? undefined,
      });
      return data.access_token as string;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * fetch wrapper that carries the authenticated JWT and, on a 401 (expired
 * token), refreshes once and retries. All data helpers route through this so
 * the whole app inherits transparent session refresh. `authorized:false` skips
 * the Authorization header (e.g. Storage uploads that set their own).
 */
async function req(
  path: string,
  init: Omit<RequestInit, 'headers'> & { headers?: Record<string, string>; authorized?: boolean } = {},
): Promise<Response> {
  const { authorized = true, headers: extra = {}, ...rest } = init;
  const build = (): Record<string, string> =>
    authorized ? authHeaders(extra) : { apikey: SUPABASE_KEY, ...extra };
  let res = await fetch(`${SUPABASE_URL}${path}`, { ...rest, headers: build() });
  if (res.status === 401 && authorized) {
    const fresh = await refreshAccessToken();
    if (fresh) res = await fetch(`${SUPABASE_URL}${path}`, { ...rest, headers: build() });
  }
  return res;
}

/** Proactively refresh the session on app load / interval so tokens never
 *  lapse mid-use. Safe no-op when logged out. Returns true if a token is live. */
/** Decode a JWT's exp (seconds since epoch); null if unparseable. */
function jwtExp(token: string | null): number | null {
  if (!token) return null;
  try { return JSON.parse(atob(token.split('.')[1]))?.exp ?? null; } catch { return null; }
}
/** True if the token is missing or already past its exp. */
export function isTokenExpired(token: string | null): boolean {
  const exp = jwtExp(token);
  return exp == null || exp * 1000 <= Date.now();
}
/** True if the token expires within `sec` seconds (default 5 min). */
function expiresWithin(token: string | null, sec = 300): boolean {
  const exp = jwtExp(token);
  return exp == null || exp * 1000 - Date.now() < sec * 1000;
}

/**
 * Ensure a NON-EXPIRED access token. Skips the network if the current token is
 * still healthy (>5 min left); otherwise exchanges the refresh token. Returns
 * TRUE only if we end up with a live token — FALSE means the session is dead
 * (refresh token also expired) and the caller should force re-login. Previously
 * this returned true whenever *any* token existed, which masked long-stale
 * sessions and left pages silently empty under RLS.
 */
export async function ensureFreshSession(): Promise<boolean> {
  const cur = useAuthStore.getState().accessToken;
  if (cur && !expiresWithin(cur)) return true; // healthy, no refresh needed
  await refreshAccessToken();
  const tok = useAuthStore.getState().accessToken;
  return !isTokenExpired(tok);
}

// ---------------------------------------------------------------------------
// 🔴 WHY NO SCREEN STAMPS THE ACTOR ON A CHANGE, AND WHAT WOULD FIX IT
// (investigated 29 Sep 2026 against live dmhw; nothing here was applied — this lane is frontend
// only. Written at the write path because this is where the next person will try to add it.)
//
// THE ASK was: the audit trail records WHAT changed without recording WHO, so make the app set
// `rainmaker.actor`. THE ANSWER IS THAT THE APP CANNOT, and building something that looks like it
// does would be worse than leaving the gap visible.
//
// WHAT WAS MEASURED, on live:
//  • public.fn_audit_trigger() is on 78 tables. For every write it reads, in this order:
//      changed_by            ← request.jwt.claims ->> 'sub'      (PostgREST sets this per request)
//      changed_by_app_user   ← current_app_user_id()
//      changed_by_email      ← rainmaker_users, looked up from one of the two above
//      changed_by_actor      ← current_setting('rainmaker.actor', true)      ← the gap
//      change_reason         ← current_setting('request.headers')->>'x-change-reason'
//  • So the JWT-derived identity ALREADY WORKS for app writes. Of the 83 rows written since 0359
//    went live (21 Sep 22:40), 27 carry a real signed-in email. `rainmaker.actor` is a SECOND,
//    separate field naming a non-human agent — a lane, a job, a script. 63 rows carry one, every
//    one of them set by Lane A's own SQL sessions.
//
// WHY A POSTGREST CLIENT CANNOT SET IT. `rainmaker.actor` is a session/transaction GUC. It is set
// by `set_config(...)`, which is SQL. This app speaks to PostgREST over HTTP: one request is one
// transaction on a pooled connection, and there is no way to send a `set_config` alongside a POST
// or a PATCH to a table. There is no header, no query parameter and no body field that PostgREST
// turns into that GUC. Adding a `changed_by_actor` field to a write body would not help either —
// the trigger overwrites it from the GUC, so the column would carry whatever the client claimed
// while the audit row's hash was computed over the client's claim. That is a field that LOOKS
// authoritative and is unverified, which is the exact failure this work exists to avoid.
//
// 🔴 WHAT LANE A OWES — and the cheap option is much better than the one that was assumed:
//
//  OPTION A (recommended — one function, no call sites, actor is VERIFIED).
//  `public.pgrst_pre_request()` already exists and is already wired: the `authenticator` role
//  carries `pgrst.db_pre_request=public.pgrst_pre_request`, so PostgREST runs it before EVERY
//  request, and it already parses request.jwt.claims (it uses them today to force
//  transaction_read_only on for a reviewer seat). Setting `rainmaker.actor` there, transaction-
//  local, from the claims it has already parsed would stamp every one of the 78 audited tables
//  at once — no screen changes, no RPC signatures, and the value comes from a signed JWT rather
//  than from anything the browser typed. It must stay guarded exactly as the read_only branch is:
//  malformed or absent claims must leave the request untouched, never raise.
//
//  OPTION B (the shape the brief assumed — a `p_actor text` parameter on each write RPC, doing
//  set_config inside). It works, but it is worse on both counts: many call sites instead of one,
//  and it trusts a string the client supplies, so a caller could stamp someone else's name on a
//  change. Only worth doing for a caller that is NOT a signed-in browser session.
//
// UNTIL ONE OF THOSE LANDS, the honest position is the one the audit screen now states: changes
// made through the app since 21 Sep carry the signed-in person's email, changes with no person at
// all are the gap, and nothing in this file pretends otherwise.
//
// ⚠️ ONE CLIENT-SETTABLE SEAM DOES ALREADY EXIST AND IS UNUSED: the trigger reads the
// `x-change-reason` request header (capped at 500 chars) into audit_trail.change_reason. Three
// rows out of 508 carry one. No screen sends it. That is a real, already-built hook for "why did
// you change this" — but nobody has asked for it, so it is recorded here rather than built.
// ---------------------------------------------------------------------------

export async function sandbox<T = any>(table: string, params = ''): Promise<T[]> {
  // Not-ready guard: a filter with an EMPTY value (e.g. `tenant_id=eq.` before the
  // session has resolved its tenant) makes PostgREST cast '' → uuid → 400, which
  // surfaces as a scary "load failure" mid-render. An empty scope means "nothing
  // scoped yet", so return no rows; the caller re-fetches once its deps (the
  // resolved tenant/id) are non-empty. Prevents the empty-tenant 400 across every
  // screen that filters by `=eq.${id}`.
  if (/=(eq|in|is)\.(?=&|$)/.test(params)) return [];
  const res = await req(`/rest/v1/${table}${params ? '?' + params : ''}`);
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

/**
 * Coerce empty-string foreign-key/uuid fields (`*_id`, or `id`) to null before a
 * write. An unselected OPTIONAL dropdown submits '' which Postgres rejects as an
 * invalid uuid (`invalid input syntax for type uuid: ""`) and 500s the form — the
 * single most common on-screen crash. An empty relation means "not set" → null.
 * Only touches keys ending in `_id` (or exactly `id`), so text fields where '' is
 * a valid value are never altered. Handles a single row or an array of rows.
 * A REQUIRED FK left blank now null-fails the NOT-NULL constraint (a clear,
 * correct error) instead of the cryptic uuid cast.
 */
export function nullifyEmptyIds<T>(body: T): T {
  const scrub = (o: any): any => {
    if (o == null || typeof o !== 'object') return o;
    const r: any = {};
    for (const k in o) {
      const v = o[k];
      r[k] = (v === '' && (k === 'id' || k.endsWith('_id'))) ? null : v;
    }
    return r;
  };
  return (Array.isArray(body) ? body.map(scrub) : scrub(body)) as T;
}

/** True if a PostgREST filter string carries an empty operator value (e.g. an
 *  unresolved `id=eq.`), which on a write would 400 (uuid cast) or, worse, match
 *  every row. Writes must never fire with an empty key filter. */
function hasEmptyFilter(params: string): boolean {
  return /=(eq|in|is)\.(?=&|$)/.test(params);
}

/**
 * Upsert row(s) into a table. PostgREST resolves conflicts on the PRIMARY KEY by
 * default; to merge on a different UNIQUE constraint, pass its columns via
 * `onConflict` (comma-separated), e.g. 'tenant_id,user_id,period_year,period_month'.
 */
export async function sandboxUpsert<T = any>(table: string, body: object, onConflict?: string): Promise<void> {
  const q = onConflict ? `?on_conflict=${encodeURIComponent(onConflict)}` : '';
  const res = await req(`/rest/v1/${table}${q}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Prefer: 'resolution=merge-duplicates,return=minimal' },
    body: JSON.stringify(nullifyEmptyIds(body)),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

/**
 * Plain INSERT (no merge). For append-only tables where the role may INSERT but not UPDATE — a merge-duplicates
 * upsert asks PostgREST for ON CONFLICT DO UPDATE, which such a table refuses (e.g. sales_build_reviews, 0336).
 * A duplicate on a UNIQUE key comes back as an error instead of silently overwriting.
 */
export async function sandboxInsert(table: string, body: object): Promise<void> {
  const res = await req(`/rest/v1/${table}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Prefer: 'return=minimal' },
    body: JSON.stringify(nullifyEmptyIds(body)),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

/**
 * Partial UPDATE of existing row(s) by filter — the correct primitive for
 * "change fields on a row that already exists". Unlike sandboxUpsert (which is a
 * merge-duplicates INSERT and therefore must satisfy every NOT-NULL column),
 * PATCH only touches the columns you pass, so `sandboxUpdate('tasks','id=eq.'+id,{status})`
 * needs no tenant_id/user_id. Use this instead of sandboxUpsert for updates by id.
 */
export async function sandboxUpdate<T = any>(table: string, match: string, patch: object): Promise<void> {
  if (hasEmptyFilter(match)) throw new Error(`sandboxUpdate(${table}): refusing PATCH with an empty filter value ("${match}") — would 400 or match every row`);
  const res = await req(`/rest/v1/${table}?${match}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Prefer: 'return=minimal' },
    body: JSON.stringify(nullifyEmptyIds(patch)),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

/**
 * Upload a file to a Supabase Storage bucket and return its public URL + path.
 * Bucket must exist and allow anon insert (see 'receipts' bucket).
 */
export async function sandboxUploadFile(bucket: string, path: string, file: File, opts: { upsert?: boolean } = {}): Promise<{ path: string; url: string }> {
  // upsert:false for write-once buckets (e.g. production-evidence, 0337): an overwrite asks for UPDATE, which the
  // floor is refused, and a new file name per upload is the rule there anyway.
  const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream' };
  if (opts.upsert !== false) headers['x-upsert'] = 'true';
  const res = await req(`/storage/v1/object/${bucket}/${encodeURIComponent(path)}`, {
    method: 'POST',
    headers,
    body: file,
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return { path: `${bucket}/${path}`, url: `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}` };
}

/**
 * Create a short-lived signed URL for a PRIVATE Storage object. `path` is the
 * bucket-qualified path returned by sandboxUploadFile ("bucket/key"). Private
 * buckets (e.g. lease-documents, POPIA) cannot be opened via a plain public URL,
 * so the authenticated app mints a temporary signed URL on demand.
 */
export async function sandboxSignedUrl(path: string, expiresIn = 3600): Promise<string> {
  const slash = path.indexOf('/');
  const bucket = path.slice(0, slash);
  const key = path.slice(slash + 1);
  // 🔴 ENCODE EACH SEGMENT, NEVER THE WHOLE KEY. encodeURIComponent(key) turned every '/' into '%2F'; Storage still answered 200 to the
  // sign call but the address it returned failed with 400 InvalidSignature, so the picture never loaded and the slip card said "could not be
  // opened". Measured on live 21 Oct as the demo office seat: key with %2F -> sign 200, GET 400 InvalidSignature; same key with literal
  // slashes -> sign 200, GET 200 image/png (9,545 bytes). A space or other special character inside a segment is still encoded.
  const res = await req(`/storage/v1/object/sign/${bucket}/${key.split('/').map(encodeURIComponent).join('/')}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiresIn }),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  const data = await res.json();
  // Storage returns { signedURL: "/object/sign/<bucket>/<key>?token=..." }
  return `${SUPABASE_URL}/storage/v1${data.signedURL}`;
}

/** Call a Postgres function via PostgREST RPC. */
export async function sandboxRpc<T = void>(fn: string, args: object): Promise<T> {
  const res = await req(`/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(nullifyEmptyIds(args)),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Delete rows from a table matching the given PostgREST filter params. */
export async function sandboxDelete(table: string, params: string): Promise<void> {
  if (hasEmptyFilter(params)) throw new Error(`sandboxDelete(${table}): refusing DELETE with an empty filter value ("${params}") — would delete every row`);
  const res = await req(`/rest/v1/${table}?${params}`, {
    method: 'DELETE',
    headers: { Prefer: 'return=minimal' },
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export function zar(n: number | null | undefined) {
  return n == null ? '—' : 'R ' + Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Send a transactional email via the send-email edge function (Resend). Requires the function
// deployed + RESEND_API_KEY set + a verified sender; returns {ok:false} with a friendly error until then.
export async function sendEmail(payload: { to: string | string[]; subject: string; html: string; cc?: string | string[]; reply_to?: string; attachments?: { filename: string; content: string }[] }): Promise<{ ok: boolean; id?: string | null; error?: string }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body?.error || `Could not send (${res.status})` };
    return { ok: true, id: body?.id ?? null };
  } catch (e) {
    return { ok: false, error: (e as Error).message || 'Network error' };
  }
}

export function pct(n: number | null | undefined) {
  return n == null ? '—' : Number(n).toFixed(1) + '%';
}

export function score(n: number | null | undefined) {
  return n == null ? '—' : Number(n).toFixed(1);
}

export function fmtDate(d: string | null | undefined) {
  return d ? new Date(d).toLocaleDateString('en-ZA') : '—';
}

// The one place a database error becomes a sentence a person can act on.
//
// 🔴 Since 0344 a refusal is the NORMAL answer, not a fault: the database now scopes invoices,
// payments and the money tables by role and entity, so a screen reaching past its seat gets 42501
// and a reviewer's read-only seat gets 25006. Postgres says "permission denied for table
// sales_invoices" and "cannot execute INSERT in a read-only transaction"; neither tells a person
// what to do next, and both name our tables on screen. These two are answered in the user's words.
// Anything else keeps the server's own message, which is usually the useful one ("Not checked in —
// nothing was saved"), rather than being flattened into a vague apology.
//
// It lives here because 13 screens had grown their own copy of the same extractor, and an error
// message is exactly the kind of thing that must read the same everywhere.
// 🔴 "FAILED TO FETCH" IS NOT A SENTENCE (Ravi, 00:41, standing in the dark with no signal).
// Every other refusal on this screen had been turned into words a person can act on, and the most
// common one in the field — no signal — still showed Chrome's internal string. He got
// "Not checked in — nothing was saved. Failed to fetch" and had no way to know whether the app was
// broken, his phone was broken, or he simply had no bars. It is the first of those three that he
// will assume, because the other two are not what the sentence says.
//
// The browsers all word it differently ("Failed to fetch" · "NetworkError when attempting to fetch
// resource" · "Load failed"), so match all of them AND trust navigator.onLine when it is definite.
// onLine false is reliable; onLine true is not, so it can only ever add a case, never remove one.
export const NETWORK_FAIL = /failed to fetch|networkerror|network request failed|load failed|err_internet|err_network|err_name_not_resolved/i;

export function plainError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  // 🔴 ASK THE BROWSER FIRST. This used to be one condition — `NETWORK_FAIL.test(raw) ||
  // navigator.onLine === false` — so ANY fetch failure blamed the person's phone. Ravi hit it on
  // 1 Oct signing in to app.rainmkr.co.za ON A DESKTOP, and was told his phone had no signal.
  // What was actually happening: dmhw returning HTTP 522 after ~27 seconds (Cloudflare reached
  // Supabase's edge, the origin never answered). The browser surfaces that as `Failed to fetch`,
  // which matches NETWORK_FAIL — and `navigator.onLine` was TRUE throughout. The check that would
  // have caught it was already in the condition, on the wrong side of the `||`.
  //
  // ⚠️ THIRD INSTANCE OF ONE FAULT IN THIS FILE'S AREA: a liveness test matching `/does not exist/`
  // reported our own bug as "the feature is switched off"; a message parse stopping at an escaped
  // quote hid a 42703; and this blamed the user for our outage. A REASSURING SENTENCE OVER A REAL
  // FAULT STOPS ANYBODY LOOKING FOR THE REAL FAULT. On an ordinary day a 522 from our own backend
  // would be reported by an operator as "my phone was offline" and nobody would check the server.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    // The browser agrees there is no connection. This one really is the phone.
    return 'Your phone has no signal right now, so nothing was sent. What you typed is still here — try again once you have bars.';
  }
  if (NETWORK_FAIL.test(raw)) {
    // 🔴 DO NOT SAY "TRY AGAIN" HERE. The usual cause is our side not answering, and retrying cannot
    // work — asking somebody to repeat a failing action is how they decide the system is broken.
    // Both of the good things in the old sentence are kept: nothing was sent, and nothing was lost.
    return 'We cannot reach Rainmaker right now. It is not your phone and not your signal — something on our side is not answering. Nothing you typed was lost, and nothing was sent. Tell the office rather than trying again.';
  }
  // 🔴 PARSE IT, DO NOT REGEX IT. This used to read the payload with /"message":"([^"]*)"/, and
  // `[^"]*` stops dead at the first ESCAPED quote. Postgres quotes identifiers in most of its
  // messages — `column "v_ent" does not exist`, `relation "x" does not exist`, `duplicate key value
  // violates unique constraint "y"` — so a large share of every database error this app has ever
  // shown a person was truncated to a fragment. Measured 1 Oct 2026: a real refusal reached the
  // screen as `column \` and nothing more. The operator learns nothing, and whoever they report it
  // to learns nothing either; I only recovered the real message by calling the RPC by hand.
  // A regex too tight to match its own subject — the same fault as a liveness test too loose to
  // miss, and it cost the same thing: a green light over something broken.
  let code = '';
  let message = raw;
  try {
    const parsed = JSON.parse(raw) as { code?: unknown; message?: unknown };
    if (typeof parsed?.message === 'string') message = parsed.message;
    if (typeof parsed?.code === 'string') code = parsed.code;
  } catch {
    // Not JSON — some errors arrive as plain text, and a few wrap JSON in other text. Fall back to
    // a pattern that DOES tolerate escapes rather than to the one that started this.
    const pick = (key: string) => {
      const m = new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`).exec(raw);
      return m?.[1]?.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    };
    code = pick('code') ?? '';
    message = pick('message') ?? raw;
  }
  if (code === '42501' || /permission denied|row-level security/i.test(message)) {
    return 'You don’t have access to that here. The office can do it for you.';
  }
  if (code === '25006' || /read-only transaction/i.test(message)) {
    return 'This account can look, but not change anything.';
  }
  return message;
}
